import { requireTrue } from './his-value.utils';

/**
 * Converts a number to a signed integer by stripping off the fractional part.
 * @returns the integer value, or `NaN` if the input cannot be converted to a number
 */
export function toInteger( x: any ) {
    const num = Number( x );
    return isNaN( num ) ? NaN : Math.trunc( num );
}

/** Converts a number to a 32-bit ***un***signed integer, emulating Java's int behavior. */
export function toUint32( x: any ) {
    return modulo( toInteger( x ), Math.pow( 2, 32 ) );
}

/** Converts a number to a 32-bit signed integer, emulating Java's int behavior. */
export function toInt32( x: any ) {
    let uint32 = toUint32( x );
    if ( uint32 >= Math.pow( 2, 31 ) ) {
        return uint32 - Math.pow( 2, 32 );
    } else {
        return uint32;
    }
}

/** Computes the modulo of two numbers, either euclidean (default) or absolute, based on the specified type. */
export function modulo( a: number, b: number, type: 'euclidean' | 'absolute' = 'euclidean' ) {
    return type === 'euclidean' ? euclideanModulo( a, b ) : absoluteModulo( a, b );
}

/**
 * Modulo operation that returns the absolute remainder of `a` divided by `b` using `|a % b|`.
 * ```ts
 * absoluteModulo( -5, 3 )  // returns |-2| = 2 because: -5 = (-2) * 3 + (-2)
 * absoluteModulo( 5, 3 )   // returns |2|  = 2 because:  5 = 1 * 3 + 2
 * absoluteModulo( -5, -3 ) // returns |-2| = 2 because: -5 = 2 * (-3) + (-2)
 * absoluteModulo( 5, -3 )  // returns |2|  = 2 because:  5 = (-1) * (-3) + 2
 * ```
 * @throws Error if `b` is zero
 * @returns the absolute remainder of `a` divided by `b`
 */
export function absoluteModulo( a: number, b: number ) {
    requireTrue( b !== 0, 'Modulo by zero' );
    return Math.abs( a % b );
}

/**
 * Modulo operation that returns the positive euclidean remainder of `a` divided by `b` using `|a % b|`.\
 * Contrary to the absolute remainder, where `modulo(-5, 3)` would return `|-2| = 2`, the euclidean modulo returns `1`
 * because it ensures that the result is actually non-negative and less than the absolute value of `b`, without using `Math.abs()` to remove the negative sign.
 * ```ts
 * euclideanModulo( -5, 3 )  // returns 1 because: -5 = (-2) * 3 + 1
 * euclideanModulo( 5, 3 )   // returns 2 because:  5 = 1 * 3 + 2
 * euclideanModulo( -5, -3 ) // returns 1 because: -5 = 2 * (-3) + 1
 * euclideanModulo( 5, -3 )  // returns 2 because:  5 = (-1) * (-3) + 2
 * ```
 * @throws Error if `b` is zero
 * @returns the positive euclidean remainder of `a` divided by `b`
 */
export function euclideanModulo( a: number, b: number ) {
    requireTrue( b !== 0, 'Modulo by zero' );
    const m = Math.abs( b );
    return ( ( a % m ) + m ) % m; // ensure a normalized positive result
}

/** Clamps the value between a minimum and maximum range. */
export function clamp( value: number, min: number, max: number ) {
    requireTrue( min <= max, `Invalid clamp range: min (${min}) cannot be greater than max (${max})` );
    return Math.min( Math.max( value, min ), max );
}

/** 
 * Wraps `value` within `[min, max)`, cycling rather than clamping.
 * ```ts
 * wrap( 2, 0, 5 )  // returns 2 because: 2 is already in [0, 5)
 * wrap( -1, 0, 5 ) // returns 4 because: -1 wrapped in [0, 5) is 4
 * wrap( 5, 0, 5 )  // returns 0 because: 5 wrapped in [0, 5) is 0
 * // => the outer edge (5) is considered equal to the inner edge (0)
 * wrap( 6, 0, 5 )  // returns 1 because: 6 wrapped in [0, 5) is 1
 * ```
 */
export function wrap( value: number, min: number, max: number ): number {
    requireTrue( min < max, `Invalid wrap range: min (${min}) must be less than max (${max})` );
    requireTrue( isFinite( value ), `Value must be a finite number. Received: ${value}` );
    return min + euclideanModulo( value - min, max - min );
}

/**
 * Snaps a number to the nearest multiple of a specified step.\
 * The `mode` parameter determines the snapping behavior:
 * - `'floor'`: Snaps down to the nearest multiple of `step`.
 * - `'ceil'`: Snaps up to the nearest multiple of `step`.
 * - `'round'` (default): Snaps to the nearest multiple of `step`, rounding half up.
 * @throws Error if `step` is not a positive number
 * @param value The value to be snapped
 * @param step The step size to snap to
 * @param mode The rounding mode ('floor', 'ceil', 'round')
 * @returns The snapped value
 */
export function snapTo( value: number, step: number, mode: 'floor' | 'ceil' | 'round' = 'round' ) {
    requireTrue( step > 0 && isFinite( step ), `Step must be a positive, finite number. Received: ${step}` );
    const quotient = value / step;
    const snappedQuotient = round( quotient, mode );
    return snappedQuotient * step;
}

/**
 * Checks if a value is within a specified range. For open ends, use `-Infinity` or `Infinity` as the min or max.
 * @param inclusive If `true`, the range is inclusive (i.e., min and max are included). If `false` or omitted, the range is exclusive.
 * @returns `true` if the value is within the range, `false` otherwise.
 * @throws Error if `min` is greater than `max`
 */
export function isInRange( value: number, min: number, max: number, inclusive?: boolean ): boolean;
/**
 * Checks if a value is within a specified range.
 * @param value The value to check
 * @param options The range options, including min (default: `-Infinity`), max (default: `Infinity`), and inclusive (default: `false`)
 * @returns `true` if the value is within the range, `false` otherwise.
 * @throws Error if `min` is greater than `max`
 */
export function isInRange( value: number, options: { min?: number, max?: number, inclusive?: boolean } ): boolean;
export function isInRange( value: number, arg1: any, arg2?: any, inclusive: boolean = false ): boolean {
    let min: number, max: number;
    if ( typeof arg1 === 'object' && arg1 !== null ) {
        ( { min = -Infinity, max = Infinity, inclusive = false } = arg1 );
    } else {
        min = arg1;
        max = arg2;
    }
    requireTrue( min <= max, `Invalid range: min (${min}) cannot be greater than max (${max})` );
    return inclusive ? ( value >= min && value <= max ) : ( value > min && value < max );
}

// ===================================
// ---- internal helper functions ----
// ===================================

function round( value: number, mode: 'floor' | 'ceil' | 'round' ): number {
    switch ( mode ) {
        case 'floor':
            return Math.floor( value );
        case 'ceil':
            return Math.ceil( value );
        case 'round':
            return Math.round( value );
        default:
            throw new Error( `Invalid rounding mode: ${mode}` );
    }
}